/**
 * useMessaging — Hook central pour la messagerie interne d'Emplois BNSSA & MNS.
 *
 * Couvre 3 cas d'usage :
 *  - A) Recruteur → Candidat CVthèque (anonymat préservé tant que non-validé)
 *  - B) Candidat AE → Recruteur (offre AE/Intérim)
 *  - C) Suite de candidature (post-application standard)
 *
 * Stratégie :
 *  - 2 collections Lumi : conversations + messages
 *  - Filtrage côté client par participantIds (read=NO_RESTRICTIONS)
 *  - Polling toutes les 15s quand l'onglet est actif (visibilitychange aware)
 *  - Anti-spam : max 5 conversations ouvertes simultanées avec un même participant
 *  - Notif email best-effort à chaque message envoyé
 */
import { lumi } from '@/lib/lumi'
import { useEffect, useState, useCallback, useRef } from 'react'
import { emailUser_NewMessage } from '@/lib/emails'
import type { AccountRole } from '@/hooks/useAuth'
import {
  trackMessageConversationStarted,
  trackMessageSent,
  trackMessageIdentityRevealed,
} from '@/lib/analytics'

export type ConversationContextType = 'cvtheque' | 'job_ae' | 'application' | 'direct'
export type ConversationStatus = 'active' | 'archived' | 'blocked' | 'reported'
export type MessageSenderRole = 'candidate' | 'recruiter' | 'organisme' | 'admin'

export interface ConversationContext {
  type: ConversationContextType
  jobId?: string
  jobTitle?: string
  availableCandidateId?: string
  candidatePublicId?: string
  applicationId?: string
}

export interface Conversation {
  _id: string
  creator: string
  participantIds: string[]
  participantRoles: string[]
  participantNames?: string[]
  participantEmails?: string[]
  context?: ConversationContext
  subject?: string
  lastMessagePreview?: string
  lastMessageAt?: string
  lastMessageSenderId?: string
  unreadCounts?: Record<string, number>
  status: ConversationStatus
  anonymityLifted?: boolean
  blockedBy?: string
  reportedBy?: string
  reportReason?: string
  createdAt: string
  updatedAt: string
}

export interface Message {
  _id: string
  creator: string
  conversationId: string
  senderId: string
  senderRole: MessageSenderRole
  senderName?: string
  content: string
  readBy?: string[]
  isSystemMessage?: boolean
  isFlagged?: boolean
  flagReason?: string
  createdAt: string
}

const POLL_INTERVAL_MS = 15000
const MAX_OPEN_CONVERSATIONS_PER_PEER = 5

/**
 * Détection naïve d'emails / téléphones partagés (pour CVthèque anonymat préservé).
 * Retourne null si rien de suspect, sinon un motif.
 */
function detectContactLeak(content: string): string | null {
  const emailRe = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i
  const phoneRe = /(?:\+?\d[\s.-]?){8,}/
  if (emailRe.test(content)) return 'email_detected'
  if (phoneRe.test(content)) return 'phone_detected'
  return null
}

export function useMessaging(currentUserId: string | null | undefined) {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const pollingTimerRef = useRef<number | null>(null)
  const isVisibleRef = useRef<boolean>(true)

  // ─── Fetch toutes les conversations où l'utilisateur courant est participant ─
  const fetchConversations = useCallback(async () => {
    if (!currentUserId) {
      setConversations([])
      return
    }
    setLoading(true)
    try {
      const { list } = await lumi.entities.conversations.list({
        sort: { lastMessageAt: -1 },
        limit: 200,
      })
      const mine = (list as Conversation[]).filter(
        (c) => Array.isArray(c.participantIds) && c.participantIds.includes(currentUserId),
      )
      setConversations(mine)
      setError(null)
    } catch (err) {
      console.error('[useMessaging] fetch conversations failed:', err)
      setError('Impossible de charger les conversations.')
    } finally {
      setLoading(false)
    }
  }, [currentUserId])

  // ─── Polling toutes les 15s quand visible ───────────────────────────────────
  useEffect(() => {
    if (!currentUserId) return
    fetchConversations()

    const handleVisibility = () => {
      isVisibleRef.current = document.visibilityState === 'visible'
      if (isVisibleRef.current) fetchConversations()
    }
    document.addEventListener('visibilitychange', handleVisibility)

    const tick = () => {
      if (isVisibleRef.current) fetchConversations()
    }
    pollingTimerRef.current = window.setInterval(tick, POLL_INTERVAL_MS)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current)
        pollingTimerRef.current = null
      }
    }
  }, [currentUserId, fetchConversations])

  // ─── Cherche ou crée une conversation entre 2 utilisateurs ──────────────────
  const findOrCreateConversation = useCallback(
    async (params: {
      peerUserId: string
      peerRole: AccountRole
      peerName?: string
      peerEmail?: string
      currentRole: AccountRole
      currentName?: string
      currentEmail?: string
      context?: ConversationContext
      subject?: string
    }): Promise<Conversation | null> => {
      if (!currentUserId) {
        setError('Vous devez être connecté pour démarrer une conversation.')
        return null
      }
      if (params.peerUserId === currentUserId) {
        setError("Vous ne pouvez pas vous écrire à vous-même.")
        return null
      }

      try {
        // 1. Cherche une conversation active existante avec ce peer (et même contexte si fourni)
        const { list } = await lumi.entities.conversations.list({
          sort: { lastMessageAt: -1 },
          limit: 200,
        })
        const existing = (list as Conversation[]).find((c) => {
          if (!Array.isArray(c.participantIds)) return false
          if (c.status === 'blocked') return false
          if (!c.participantIds.includes(currentUserId)) return false
          if (!c.participantIds.includes(params.peerUserId)) return false
          // Même contexte si fourni (jobId / availableCandidateId)
          if (params.context?.jobId && c.context?.jobId !== params.context.jobId) return false
          if (
            params.context?.availableCandidateId &&
            c.context?.availableCandidateId !== params.context.availableCandidateId
          )
            return false
          return true
        })
        if (existing) return existing

        // 2. Anti-spam : max N conversations ouvertes avec un même peer
        const sameParticipant = (list as Conversation[]).filter(
          (c) =>
            Array.isArray(c.participantIds) &&
            c.participantIds.includes(currentUserId) &&
            c.participantIds.includes(params.peerUserId) &&
            c.status === 'active',
        )
        if (sameParticipant.length >= MAX_OPEN_CONVERSATIONS_PER_PEER) {
          setError(
            `Vous avez déjà ${MAX_OPEN_CONVERSATIONS_PER_PEER} conversations ouvertes avec cet utilisateur.`,
          )
          return null
        }

        // 3. Création
        const now = new Date().toISOString()
        const created = (await lumi.entities.conversations.create({
          creator: currentUserId,
          participantIds: [currentUserId, params.peerUserId],
          participantRoles: [params.currentRole, params.peerRole],
          participantNames: [params.currentName || '', params.peerName || ''],
          participantEmails: [params.currentEmail || '', params.peerEmail || ''],
          context: params.context || { type: 'direct' },
          subject: params.subject || '',
          status: 'active',
          anonymityLifted: params.context?.type === 'cvtheque' ? false : true,
          unreadCounts: { [currentUserId]: 0, [params.peerUserId]: 0 },
          createdAt: now,
          updatedAt: now,
        })) as Conversation

        trackMessageConversationStarted({
          context: params.context?.type,
          initiatorRole: params.currentRole,
        })

        await fetchConversations()
        return created
      } catch (err) {
        console.error('[useMessaging] findOrCreateConversation failed:', err)
        setError('Impossible de créer la conversation.')
        return null
      }
    },
    [currentUserId, fetchConversations],
  )

  // ─── Liste les messages d'une conversation ──────────────────────────────────
  const listMessages = useCallback(
    async (conversationId: string): Promise<Message[]> => {
      try {
        const { list } = await lumi.entities.messages.list({
          filter: { conversationId },
          sort: { createdAt: 1 },
          limit: 500,
        })
        return list as Message[]
      } catch (err) {
        console.error('[useMessaging] listMessages failed:', err)
        return []
      }
    },
    [],
  )

  // ─── Envoie un message dans une conversation existante ──────────────────────
  const sendMessage = useCallback(
    async (params: {
      conversation: Conversation
      content: string
      currentRole: MessageSenderRole
      currentName?: string
    }): Promise<Message | null> => {
      if (!currentUserId) return null
      const trimmed = params.content.trim()
      if (!trimmed) return null
      if (trimmed.length > 4000) {
        setError('Message trop long (4000 caractères max).')
        return null
      }
      if (params.conversation.status === 'blocked') {
        setError('Cette conversation a été bloquée.')
        return null
      }

      // Détection contact leak en CVthèque non-validée
      let flagReason: string | null = null
      if (
        params.conversation.context?.type === 'cvtheque' &&
        !params.conversation.anonymityLifted
      ) {
        flagReason = detectContactLeak(trimmed)
      }

      const now = new Date().toISOString()
      try {
        const peerId = params.conversation.participantIds.find((id) => id !== currentUserId) || ''
        const newUnread = { ...(params.conversation.unreadCounts || {}) }
        newUnread[peerId] = (newUnread[peerId] || 0) + 1
        newUnread[currentUserId] = 0

        // 1. Crée le message
        const msg = (await lumi.entities.messages.create({
          creator: currentUserId,
          conversationId: params.conversation._id,
          senderId: currentUserId,
          senderRole: params.currentRole,
          senderName: params.currentName || '',
          content: trimmed,
          readBy: [currentUserId],
          isSystemMessage: false,
          isFlagged: !!flagReason,
          flagReason: flagReason || '',
          createdAt: now,
        })) as Message

        // 2. Met à jour la conversation (preview, lastMessage*, unreadCounts)
        await lumi.entities.conversations.update(params.conversation._id, {
          lastMessagePreview: trimmed.slice(0, 200),
          lastMessageAt: now,
          lastMessageSenderId: currentUserId,
          unreadCounts: newUnread,
          updatedAt: now,
        })

        // 3. Notif email au destinataire (best-effort)
        const peerIdx = params.conversation.participantIds.indexOf(peerId)
        const peerEmail = params.conversation.participantEmails?.[peerIdx]
        const peerName = params.conversation.participantNames?.[peerIdx]
        if (peerEmail) {
          let senderDisplay = params.currentName || 'Un utilisateur'
          // En CVthèque non-validée, le candidat reste anonyme côté recruteur
          if (
            params.conversation.context?.type === 'cvtheque' &&
            !params.conversation.anonymityLifted &&
            params.currentRole === 'candidate'
          ) {
            senderDisplay = `Candidat ${params.conversation.context.candidatePublicId || ''}`.trim()
          }
          const ctx = params.conversation.context
          const ctxLabel =
            ctx?.jobTitle
              ? `l'offre « ${ctx.jobTitle} »`
              : ctx?.type === 'cvtheque'
                ? 'votre profil dans la CVthèque'
                : undefined
          emailUser_NewMessage({
            recipientEmail: peerEmail,
            recipientName: peerName,
            senderDisplayName: senderDisplay,
            senderRole:
              params.currentRole === 'admin' ? 'recruiter' : (params.currentRole as AccountRole),
            messagePreview: trimmed,
            conversationContext: ctxLabel,
            conversationUrl: `https://bnssa-mns.fr/messages?c=${params.conversation._id}`,
          }).catch((e) => console.warn('[useMessaging] email notif failed:', e))
        }

        trackMessageSent({
          context: params.conversation.context?.type,
          senderRole: params.currentRole,
          anonymityLifted: params.conversation.anonymityLifted,
        })

        await fetchConversations()
        return msg
      } catch (err) {
        console.error('[useMessaging] sendMessage failed:', err)
        setError("Impossible d'envoyer le message.")
        return null
      }
    },
    [currentUserId, fetchConversations],
  )

  // ─── Marque les messages d'une conversation comme lus pour l'utilisateur ───
  const markAsRead = useCallback(
    async (conversationId: string) => {
      if (!currentUserId) return
      const conv = conversations.find((c) => c._id === conversationId)
      if (!conv) return
      const newUnread = { ...(conv.unreadCounts || {}) }
      if (newUnread[currentUserId] === 0) return
      newUnread[currentUserId] = 0
      try {
        await lumi.entities.conversations.update(conversationId, {
          unreadCounts: newUnread,
          updatedAt: new Date().toISOString(),
        })
        setConversations((prev) =>
          prev.map((c) => (c._id === conversationId ? { ...c, unreadCounts: newUnread } : c)),
        )
      } catch (err) {
        console.error('[useMessaging] markAsRead failed:', err)
      }
    },
    [currentUserId, conversations],
  )

  // ─── Le candidat révèle son identité au recruteur (CVthèque) ────────────────
  const liftAnonymity = useCallback(
    async (params: {
      conversation: Conversation
      currentName?: string
      firstName?: string
      lastName?: string
      phone?: string
      email?: string
    }): Promise<boolean> => {
      if (!currentUserId) return false
      const conv = params.conversation
      if (conv.anonymityLifted) return true
      const now = new Date().toISOString()

      // Nom affiché : priorité au prénom+nom, sinon currentName
      const composedName =
        [params.firstName, params.lastName].filter(Boolean).join(' ').trim() ||
        (params.currentName || '').trim()
      const displayName = composedName || 'Identité confirmée'

      try {
        // 1. Lève l'anonymat sur la conversation + inscrit le vrai nom du candidat
        const newNames = [...(conv.participantNames || [])]
        const meIdx = conv.participantIds.indexOf(currentUserId)
        if (meIdx >= 0 && composedName) newNames[meIdx] = composedName
        await lumi.entities.conversations.update(conv._id, {
          anonymityLifted: true,
          participantNames: newNames,
          updatedAt: now,
        })

        // 2. Message système informant le recruteur (coordonnées complètes)
        const peerId = conv.participantIds.find((id) => id !== currentUserId) || ''
        const newUnread = { ...(conv.unreadCounts || {}) }
        newUnread[peerId] = (newUnread[peerId] || 0) + 1

        const lines: string[] = [
          'Le candidat a choisi de révéler son identité. Voici ses coordonnées :',
        ]
        lines.push(`• Nom : ${displayName}`)
        if (params.phone?.trim()) lines.push(`• Téléphone : ${params.phone.trim()}`)
        if (params.email?.trim()) lines.push(`• Email : ${params.email.trim()}`)
        const revealText = lines.join('\n')

        await lumi.entities.messages.create({
          creator: currentUserId,
          conversationId: conv._id,
          senderId: currentUserId,
          senderRole: 'candidate',
          senderName: composedName,
          content: revealText,
          readBy: [currentUserId],
          isSystemMessage: true,
          isFlagged: false,
          flagReason: '',
          createdAt: now,
        })
        await lumi.entities.conversations.update(conv._id, {
          lastMessagePreview: `Identité révélée : ${displayName}`.slice(0, 200),
          lastMessageAt: now,
          lastMessageSenderId: currentUserId,
          unreadCounts: newUnread,
          updatedAt: now,
        })

        trackMessageIdentityRevealed({ context: conv.context?.type })

        await fetchConversations()
        return true
      } catch (err) {
        console.error('[useMessaging] liftAnonymity failed:', err)
        setError("Impossible de révéler votre identité pour le moment.")
        return false
      }
    },
    [currentUserId, fetchConversations],
  )

  // ─── Bloque / Signale une conversation ──────────────────────────────────────
  const blockConversation = useCallback(
    async (conversationId: string) => {
      if (!currentUserId) return
      try {
        await lumi.entities.conversations.update(conversationId, {
          status: 'blocked',
          blockedBy: currentUserId,
          updatedAt: new Date().toISOString(),
        })
        await fetchConversations()
      } catch (err) {
        console.error('[useMessaging] blockConversation failed:', err)
      }
    },
    [currentUserId, fetchConversations],
  )

  const reportConversation = useCallback(
    async (conversationId: string, reason: string) => {
      if (!currentUserId) return
      try {
        await lumi.entities.conversations.update(conversationId, {
          status: 'reported',
          reportedBy: currentUserId,
          reportReason: reason,
          updatedAt: new Date().toISOString(),
        })
        await fetchConversations()
      } catch (err) {
        console.error('[useMessaging] reportConversation failed:', err)
      }
    },
    [currentUserId, fetchConversations],
  )

  // ─── Compteur global de non-lus pour le badge header ────────────────────────
  const totalUnread = currentUserId
    ? conversations.reduce((sum, c) => sum + (c.unreadCounts?.[currentUserId] || 0), 0)
    : 0

  return {
    conversations,
    loading,
    error,
    totalUnread,
    fetchConversations,
    findOrCreateConversation,
    listMessages,
    sendMessage,
    liftAnonymity,
    markAsRead,
    blockConversation,
    reportConversation,
  }
}
